import React, { useRef, useMemo, useState } from 'react';
import { IconButton, Avatar, Checkbox, Tag, StatusTag } from '~/UIKit';
import {
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
} from '@mui/material';
import { IBookingSimple, PresenceType, BookingStatus } from '~/Models/DTO/Booking.models';
import { getInitials } from '~/Helpers/String.helper';
import { CLOUDINARY_URLS } from '~/Constants/Urls.constants';
import {
  BORDER_COLOR,
  TEXT_SUBHEADER,
  CHARCOAL_GREY,
  FITOGRAM_BLUE_FADED_10,
} from '~/Constants/Colors.constants';
import { CheckIn } from '~/Components/Icons';
import { useBookingListItem } from './useBookingListItem';
import { makeStyles } from '~/UIKit/Style';
import { OverflowingMenu, OverflowingMenuSensitivity } from '~/Components';
import { AccessList, useHasAccess, useLanguage } from '~/Hooks';
import { USCCheckInStatus } from './USCCheckInStatus';
import { RefundConfirmationModal } from './RefundConfirmationModal';
import { useMenuItems } from './useMenuItems';
export interface BookingListItemProps {
  booking: IBookingSimple;
  checkInOpen: boolean;
  selectable: boolean;
  selected: boolean;
  markPresence: (booking: IBookingSimple, type: PresenceType) => void;
  updateBookingStatus: (booking: IBookingSimple, status: BookingStatus) => void;
  onSelected: (uuid: string) => void;
}

const useStyles = (checkInOpen: boolean) =>
  makeStyles((theme) => ({
    root: {
      minHeight: 60,
      padding: '4px 16px',
      borderBottom: `1px solid ${BORDER_COLOR}`,
    },
    listItemName: {
      fontSize: 16,
      overflow: 'hidden',
      fontWeight: 'bold',
      textOverflow: 'ellipsis',
      wordBreak: 'break-word',
    },
    listItemDescriptions: {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      color: TEXT_SUBHEADER,
    },
    listItemContainer: {
      overflow: 'hidden',
      marginTop: 4,
      whiteSpace: 'nowrap',
      marginBottom: 4,
      paddingRight: 10,
      letterSpacing: 0.5,
      [theme.breakpoints.up('md')]: {
        whiteSpace: 'pre-wrap',
      },
    },
    avatarListItem: {
      minWidth: 45,
      height: '100%',
    },
    avatar: {
      width: 30,
      height: 30,
      fontSize: 12,
      fontWeight: 'bold',
    },
    content: {
      width: '100%',
      display: 'flex',
      overflow: 'hidden',
      alignItems: 'center',
      paddingRight: 15,
      paddingLeft: 1,
    },
    listItemAction: {
      pointerEvents: checkInOpen ? 'initial' : 'none',
      filter: checkInOpen ? '' : 'grayscale(0.9)',
      display: 'flex',
      alignItems: 'center',
      width: 'max-content',
      height: '100%',
      position: 'relative',
      top: 0,
      right: 0,
      transform: 'none',
    },
    tagsContainer: {
      display: 'flex',
      overflow: 'hidden',
      alignItems: 'center',
    },
    tags: {
      display: 'flex',
      alignItems: 'center',
      overflow: 'hidden',
      overflowX: 'auto',
      flexWrap: 'nowrap',
      whiteSpace: 'nowrap',
      paddingBottom: 8,
      marginBottom: -5,
      marginTop: -2,
      [theme.breakpoints.up('md')]: {
        flexWrap: 'wrap',
        whiteSpace: 'pre-wrap',
        paddingBottom: 0,
        margin: 'auto 0',
      },
      '& > *:last-of-type': {
        marginRight: 15,
      },
    },
    cancellationTags: {
      backgroundColor: FITOGRAM_BLUE_FADED_10,
      color: CHARCOAL_GREY,
      border: 'none',
      marginTop: 0,
      marginBottom: 0,
      padding: '4px 12px;',
    },
  }));

export const BookingListItem = ({
  booking,
  checkInOpen,
  markPresence,
  selectable,
  selected,
  onSelected,
  updateBookingStatus,
}: BookingListItemProps) => {
  const menuAnchor = useRef(null);
  const { classes } = useStyles(checkInOpen)();
  const { t } = useLanguage();
  const [showRefundModal, setShowRefundModal] = useState(false);

  const {
    handleSelect,
    labelId,
    handleMarkNeutral,
    handleActionButton,
    handleAddProduct,
    handleBookingUpdate,
    productType,
    remainingCredits,
    Tags,
    paymentStatus,
  } = useBookingListItem({
    booking,
    markPresence,
    selectable,
    onSelected,
    updateBookingStatus,
  });

  const { hasAccess } = useHasAccess();

  const viewMode =
    hasAccess(AccessList.CHECKIN_PP_STATUS) ||
    ['usc'].includes(booking?.origin?.toLocaleLowerCase());

  const hasManualBookingAccess = hasAccess(AccessList.MANUAL_BOOKING);

  const menuItems = useMenuItems({
    booking,
    hasManualBookingAccess,
    handleActionButton,
    handleAddProduct,
    handleBookingUpdate,
    setShowRefundModal,
    updateBookingStatus,
    productType,
    viewMode,
  });

  const statusIcon = useMemo(
    () =>
      viewMode ? (
        <USCCheckInStatus presenceType={booking.presenceType} status={booking.status} />
      ) : (
        <div onClick={(ev) => ev.stopPropagation()} data-testid={'BookingListItemContextMenu'}>
          <ListItemSecondaryAction className={classes.listItemAction}>
            {booking.presenceType === PresenceType.PRESENT && (
              <IconButton
                disabled={!hasManualBookingAccess}
                onClick={handleMarkNeutral}
                size="large"
              >
                <CheckIn />
              </IconButton>
            )}
            {hasManualBookingAccess && (
              <OverflowingMenu
                menuItems={menuItems}
                sensitivity={
                  booking.presenceType === PresenceType.PRESENT
                    ? OverflowingMenuSensitivity.HIGH
                    : OverflowingMenuSensitivity.LOW
                }
              />
            )}
          </ListItemSecondaryAction>
        </div>
      ),
    [booking, viewMode, productType],
  );

  const tagSection = useMemo(() => {
    const { status, cancelledLate, cancelledWithRefund, createdByUid } = booking;
    const isCancelled = status === BookingStatus.CANCELLED;
    return (
      <div className={classes.tagsContainer}>
        <div className={classes.tags}>
          {isCancelled ? (
            <>
              {cancelledLate && (
                <Tag key={`CANCELLED_LATE_${createdByUid}`} customCss={classes.cancellationTags}>
                  {t('app.checkin.tags.cancelledLate')}
                </Tag>
              )}
              {cancelledWithRefund && (
                <Tag
                  key={`CANCELLED_WITH_REFAUND${createdByUid}`}
                  customCss={classes.cancellationTags}
                >
                  {t('app.checkin.tags.cancelledWithRefund')}
                </Tag>
              )}
            </>
          ) : (
            Tags?.map((tag) => (
              <Tag key={`CUSTOMER_${tag.customerId}TAG_${tag.id}`}>{tag.name}</Tag>
            ))
          )}
          {paymentStatus === 'Due' && (
            <StatusTag status={'danger'} size="small">
              {t('app.booking.paymentStatus.duo')}
            </StatusTag>
          )}
        </div>
      </div>
    );
  }, [Tags, booking.cancelledLate, booking.cancelledWithRefund, paymentStatus]);

  return (
    <ListItem
      button
      className={classes.root}
      dense
      key={booking.uuid}
      onClick={handleSelect}
      ref={menuAnchor}
      data-testid={'BookingListItem'}
    >
      {selectable && (
        <ListItemIcon>
          <Checkbox
            checked={selected}
            color="primary"
            disableRipple
            edge="start"
            inputProps={{ 'aria-labelledby': labelId }}
          />
        </ListItemIcon>
      )}
      <ListItemAvatar className={classes.avatarListItem}>
        <Avatar
          className={classes.avatar}
          src={
            booking?.customer?.imageId
              ? `${CLOUDINARY_URLS.AVATAR_SMALL}${booking.customer.imageId}`
              : undefined
          }
        >
          {getInitials(booking.customer.firstname, booking.customer.lastname)}
        </Avatar>
      </ListItemAvatar>
      <ListItemText>
        <div className={classes.content}>
          <ListItemText
            id={labelId}
            primary={`${booking.customer.firstname} ${booking.customer.lastname}`}
            classes={{
              primary: classes.listItemName,
              secondary: classes.listItemDescriptions,
              multiline: classes.listItemContainer,
            }}
            secondary={`${booking.orderLine?.text || ''}${remainingCredits}`}
          />
        </div>
        {tagSection}
      </ListItemText>
      {statusIcon}
      <ListItemSecondaryAction>
        <RefundConfirmationModal
          open={showRefundModal}
          onClose={() => setShowRefundModal(false)}
          onConfirm={(cancelledWithRefund: boolean) => {
            setShowRefundModal(false);
            updateBookingStatus({ ...booking, cancelledWithRefund }, BookingStatus.CANCELLED);
          }}
        />
      </ListItemSecondaryAction>
    </ListItem>
  );
};
